import * as THREE from 'three';
import type { System } from '../../systems';
import type { InputManager } from '../../input';
import type { PlayerShip } from './PlayerShip';
import type { EngineEffect } from '../../visual';
import type { AudioManager } from '../../audio';
import type { PlayerStats } from '../player';

const FUEL_CONSUMPTION_PER_SECOND = 0.07;

export interface ShipControlConfig {
  readonly acceleration: number;
  readonly maxSpeed: number;
  readonly damping: number;
  readonly maxRoll: number;
  readonly rollResponse: number;
  readonly mouseSensitivity: number;
  readonly maxPitch: number;
  readonly turnResponse: number;
  readonly bankAmount: number;
  readonly maxBank: number;
}

export class ShipControlSystem implements System {
  readonly id = 'ship-control';

  private readonly forward = new THREE.Vector3();
  private readonly engineWorld = new THREE.Vector3();
  private readonly euler = new THREE.Euler(0, 0, 0, 'YXZ');
  private targetYaw = 0;
  private targetPitch = 0;
  private yaw = 0;
  private pitch = 0;
  private roll = 0;
  private bankRoll = 0;
  private enabled = true;

  constructor(
    private readonly ship: PlayerShip,
    private readonly input: InputManager,
    private readonly config: ShipControlConfig,
    private readonly trails: readonly EngineEffect[] = [],
    private readonly audio: AudioManager | null = null,
    private readonly stats: PlayerStats | null = null
  ) {
    this.syncFromShip();
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.ship.setThrust(0);
  }

  syncFromShip(): void {
    this.euler.setFromQuaternion(this.ship.object3D.quaternion, 'YXZ');
    this.yaw = this.euler.y;
    this.pitch = this.euler.x;
    this.targetYaw = this.yaw;
    this.targetPitch = this.pitch;
    this.roll = 0;
    this.bankRoll = 0;
  }

  update(dt: number): void {
    if (!this.enabled) {
      this.applyDamping(dt);
      this.integrate(dt);
      // While the autopilot (e.g. hyperspace jump) is driving the ship it
      // sets ship.thrust directly. Keep emitting engine trails so the visual
      // effect tracks the actual motion, not the (disabled) input state.
      this.emitTrailsFromShipThrust(dt);
      return;
    }

    if (this.input.isPointerLocked()) {
      const dx = this.input.getPointerDeltaX();
      const dy = this.input.getPointerDeltaY();
      this.targetYaw -= dx * this.config.mouseSensitivity;
      this.targetPitch -= dy * this.config.mouseSensitivity;
      this.targetPitch = THREE.MathUtils.clamp(
        this.targetPitch,
        -this.config.maxPitch,
        this.config.maxPitch
      );
    }

    const turnAlpha = 1 - Math.exp(-this.config.turnResponse * dt);
    this.yaw += (this.targetYaw - this.yaw) * turnAlpha;
    this.pitch += (this.targetPitch - this.pitch) * turnAlpha;

    const rollInput =
      (this.input.isKeyDown('KeyA') ? 1 : 0) -
      (this.input.isKeyDown('KeyD') ? 1 : 0);
    const targetRoll = rollInput * this.config.maxRoll;
    const rollAlpha = 1 - Math.exp(-this.config.rollResponse * dt);
    this.roll += (targetRoll - this.roll) * rollAlpha;

    const yawError = this.targetYaw - this.yaw;
    const bankTarget = THREE.MathUtils.clamp(
      -yawError * this.config.bankAmount,
      -this.config.maxBank,
      this.config.maxBank
    );
    const bankAlpha = 1 - Math.exp(-5 * dt);
    this.bankRoll += (bankTarget - this.bankRoll) * bankAlpha;

    let thrust =
      (this.input.isKeyDown('KeyW') ? 1 : 0) -
      (this.input.isKeyDown('KeyS') ? 1 : 0);

    if (thrust !== 0 && this.stats) {
      if (this.stats.fuel <= 0) {
        thrust = 0;
      } else {
        this.stats.consumeFuel(Math.abs(thrust) * FUEL_CONSUMPTION_PER_SECOND * dt);
      }
    }

    const body = this.ship.object3D;
    body.rotation.order = 'YXZ';
    this.euler.set(this.pitch, this.yaw, this.roll + this.bankRoll, 'YXZ');
    body.quaternion.setFromEuler(this.euler);

    this.forward.set(0, 0, -1).applyQuaternion(body.quaternion);
    const velocity = this.ship.velocity;
    velocity.addScaledVector(
      this.forward,
      thrust * this.config.acceleration * dt
    );

    this.applyDamping(dt);
    const speed = velocity.length();
    if (speed > this.config.maxSpeed) {
      velocity.multiplyScalar(this.config.maxSpeed / speed);
    }

    body.position.addScaledVector(velocity, dt);

    const thrustLevel =
      thrust > 0 ? Math.min(speed / this.config.maxSpeed + 0.25, 1) : 0;
    this.ship.setThrust(thrustLevel);
    this.audio?.setEngineLevel(Math.min(speed / this.config.maxSpeed, 1));

    if (this.trails.length > 0 && thrustLevel > 0.05) {
      body.updateMatrixWorld();
      const engines = this.ship.engines;
      const limit = Math.min(this.trails.length, engines.length);
      for (let i = 0; i < limit; i++) {
        const engine = engines[i];
        const trail = this.trails[i];
        if (!engine || !trail) continue;
        this.engineWorld.copy(engine.local).applyMatrix4(body.matrixWorld);
        trail.emit(this.engineWorld, thrustLevel, dt);
      }
    }
  }

  private applyDamping(dt: number): void {
    const dampCoef = Math.pow(this.config.damping, dt * 60);
    this.ship.velocity.multiplyScalar(dampCoef);
  }

  private integrate(dt: number): void {
    this.ship.object3D.position.addScaledVector(this.ship.velocity, dt);
  }

  private emitTrailsFromShipThrust(dt: number): void {
    const thrustLevel = this.ship.getThrust();
    if (this.trails.length === 0 || thrustLevel <= 0.05) return;
    const body = this.ship.object3D;
    body.updateMatrixWorld();
    const engines = this.ship.engines;
    const limit = Math.min(this.trails.length, engines.length);
    for (let i = 0; i < limit; i++) {
      const engine = engines[i];
      const trail = this.trails[i];
      if (!engine || !trail) continue;
      this.engineWorld.copy(engine.local).applyMatrix4(body.matrixWorld);
      trail.emit(this.engineWorld, thrustLevel, dt);
    }
  }
}
